import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { useProductTour } from "@/lib/product-tour/use-product-tour";
import type { TourStep } from "@/lib/product-tour/tour-steps";
import { ProductTour } from "./ProductTour";

export type TourDaPagina = {
  steps: TourStep[];
  onBeforeStart?: () => void;
};

type ProductTourContextValue = {
  start: () => void;
  startPageTour: (steps: TourStep[]) => void;
  available: boolean;
  justCompletedOnboarding: boolean;
  dismissCallout: () => void;
  /** O tour da página que está aberta agora, se ela tiver um. Quem registra é o `PageTourEntry`;
   *  quem oferece ao usuário é o menu do perfil. */
  tourDaPagina: TourDaPagina | null;
  registrarTourDaPagina: (tour: TourDaPagina | null) => void;
};

const ProductTourContext = createContext<ProductTourContextValue | null>(null);

export function ProductTourProvider({ children }: { children: ReactNode }) {
  const {
    steps,
    globalSteps,
    run,
    stepIndex,
    start,
    startCustom,
    handleEvent,
    justCompletedOnboarding,
    dismissCallout,
  } = useProductTour();

  const [tourDaPagina, setTourDaPagina] = useState<TourDaPagina | null>(null);
  const registrarTourDaPagina = useCallback((tour: TourDaPagina | null) => {
    setTourDaPagina(tour);
  }, []);

  return (
    <ProductTourContext.Provider
      value={{
        start,
        startPageTour: startCustom,
        available: globalSteps.length > 0,
        justCompletedOnboarding,
        dismissCallout,
        tourDaPagina,
        registrarTourDaPagina,
      }}
    >
      {children}
      <ProductTour steps={steps} run={run} stepIndex={stepIndex} onEvent={handleEvent} />
    </ProductTourContext.Provider>
  );
}

export function useProductTourContext() {
  const ctx = useContext(ProductTourContext);
  if (!ctx) throw new Error("useProductTourContext must be used within ProductTourProvider");
  return ctx;
}
